import { Theme, useMediaQuery } from "@mui/material";
import { createContext, useContext } from "react";
import { offsetBreakpointValues } from "../../const";
import { useContentInset } from "../useContentInset";

export type TScreenSize = "xs" | "sm" | "md" | "lg" | "xl";

/**
 * Overrides the breakpoint values `useScreenSize` measures against. Only
 * `ResponsiveWrapper` sets it — everything else measures against the real
 * breakpoints, offset by the nav rail.
 */
export const ThemeContext = createContext<Theme | null>(null);

/**
 * The screen size of the space the page actually has to lay out in: the window
 * minus whatever the nav rail is taking up. An 800px window with the rail
 * expanded (280px) reports `xs`, because only 520px are left for content; with
 * the rail collapsed (80px) the same window reports `sm` at 720px.
 *
 * The inset comes from context, so collapsing the rail re-resolves this
 * everywhere. Anything that escapes the content area — modals and drawers, the
 * top bar — wraps itself in `ViewportBreakpoints` to measure the raw window
 * instead, as does the decision of whether to show the rail at all.
 */
export const useScreenSize = () => {
    const customValues = useContext(ThemeContext)?.breakpoints.values;
    const contentInset = useContentInset();
    const values = customValues ?? offsetBreakpointValues(contentInset);

    const isSM = useMediaQuery(`(min-width:${values.sm}px)`);
    const isMD = useMediaQuery(`(min-width:${values.md}px)`);
    const isLG = useMediaQuery(`(min-width:${values.lg}px)`);
    const isXL = useMediaQuery(`(min-width:${values.xl}px)`);

    const screenSize: TScreenSize = isXL ? "xl" : isLG ? "lg" : isMD ? "md" : isSM ? "sm" : "xs";

    return {
        screenSize,
        isXS: screenSize === "xs",
        isSM: screenSize === "sm",
        isMD: screenSize === "md",
        isLG: screenSize === "lg",
        isXL: screenSize === "xl",
    };
};
